import XLSX, { WorkSheet } from 'xlsx-js-style';

export type dataField = number | string | null;

export interface IBuffer {
    buffer: string | ArrayBuffer;
}

export interface IDownload {
    header: string[];
    data: dataField[][];
    filename: string;
    sheetname: string;
}

export default class Xlsx {
    xlsx: typeof XLSX | null;

    constructor() {
        this.xlsx = null;
    }

    async _importXLSX() {
        if (!this.xlsx) this.xlsx = await import('xlsx-js-style');
    }

    async _getSheet(header: string[], data: dataField[][]) {
        await this._importXLSX();
        if (!this.xlsx) return;
        const sheet = this.xlsx.utils.aoa_to_sheet([header, ...data]);
        const colsNum = header.length;
        sheet['!cols'] = new Array(colsNum).fill({ width: 30 });
        for (let i in sheet) {
            if (typeof sheet[i] !== 'object') continue;
            const cell = this.xlsx.utils.decode_cell(i);
            sheet[i].s = {
                ...sheet[i].s,
                font: {
                    name: 'Arial',
                },
                alignment: {
                    horizontal: 'left',
                    vertical: 'center',
                    wrapText: true,
                },
            };
            if (cell.r === 0) {
                sheet[i].s = {
                    ...sheet[i].s,
                    font: {
                        ...sheet[i].s.font,
                        bold: true,
                    },
                };
            }
        }
        return sheet;
    }

    async _downloadSheet(
        sheet: WorkSheet,
        filename: string,
        sheetname: string,
    ) {
        await this._importXLSX();
        if (this.xlsx) {
            const wb = this.xlsx.utils.book_new();
            this.xlsx.utils.book_append_sheet(wb, sheet, sheetname);
            this.xlsx.writeFile(wb, `${filename}.xlsx`);
        }
    }

    async download({ header, data, filename, sheetname }: IDownload) {
        const sheet = await this._getSheet(header, data);
        if (sheet) {
            await this._downloadSheet(sheet, filename, sheetname);
        }
    }

    async downloadTemplate({
        header,
        filename,
        sheetname,
    }: Omit<IDownload, 'data'>) {
        const sheet = await this._getSheet(header, []);
        if (sheet) {
            await this._downloadSheet(sheet, filename, sheetname);
        }
    }

    async parseData<T = unknown>({ buffer }: IBuffer) {
        await this._importXLSX();
        try {
            if (!this.xlsx) return;
            const wb = this.xlsx.read(buffer);
            const sheets = wb.Sheets;
            const [name] = Object.keys(sheets);
            const sheet = sheets[name];
            const result = this.xlsx.utils.sheet_to_json<T>(sheet);
            return result;
        } catch (e) {
            return;
        }
    }
}
